import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Play } from 'lucide-react';
import { useCutList } from '../../contexts/CutListContext';
import { useOptimizer } from '../../hooks/useOptimizer';
import SheetRenderer from '../../shared/SheetRenderer';
import { UNIT_SYSTEMS } from '../../utils/units';
import { getPartColor } from '../../utils/colors';

/**
 * Step 3: Layout - View optimized layouts in a swipeable carousel
 */
const LayoutStep = ({ onNext }) => {
  const {
    pieces,
    layouts,
    stats,
    currentUnits,
    sheetWidth,
    sheetLength,
    stockSheetTypes,
    highlightedPieceId,
    setHighlightedPieceId,
  } = useCutList();

  const { optimize } = useOptimizer();
  const [currentSheet, setCurrentSheet] = useState(0);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);

  // Check orientation
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-optimize when entering this step if no layouts exist
  useEffect(() => {
    if (pieces.length > 0 && layouts.length === 0) {
      optimize();
    }
  }, [pieces.length, layouts.length, optimize]);

  const goToPrevSheet = () => {
    setCurrentSheet((prev) => Math.max(0, prev - 1));
  };

  const goToNextSheet = () => {
    setCurrentSheet((prev) => Math.min(layouts.length - 1, prev + 1));
  };

  // Swipe handling with live drag
  const touchStartX = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    setSwipeOffset(0);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current === null) return;
    const diff = e.touches[0].clientX - touchStartX.current;
    setSwipeOffset(diff);
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goToNextSheet();
      else goToPrevSheet();
    }
    setSwipeOffset(0);
    touchStartX.current = null;
  }, [layouts.length, currentSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUnitLabel = () => {
    if (currentUnits.system === UNIT_SYSTEMS.IMPERIAL) {
      return currentUnits.unit === 'inches' ? 'in' : 'ft';
    }
    return currentUnits.unit;
  };

  // Landscape suggestion for portrait mode
  return (
    <div className="flex flex-col h-full">
      {/* Stats Header */}
      {Object.keys(stats).length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-accent-400">{stats.totalSheets}</div>
              <div className="text-xs text-gray-500">Sheets</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-sage-500">{stats.efficiency}%</div>
              <div className="text-xs text-gray-500">Efficiency</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-700">{stats.placedPieces}/{stats.totalPieces}</div>
              <div className="text-xs text-gray-500">Placed</div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden p-4">
        {layouts.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center">
            <Play className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">Ready to optimize your cuts</p>
            <button
              onClick={optimize}
              disabled={pieces.length === 0}
              className="px-8 py-4 bg-accent-400 text-white rounded-xl font-bold text-lg hover:bg-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-5 h-5 inline mr-2" />
              Optimize Now
            </button>
            {pieces.length === 0 && (
              <p className="text-sm text-gray-400 mt-2">Add parts first</p>
            )}
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Dot Indicators */}
            {layouts.length > 1 && (
              <div className="flex justify-center items-center gap-2 mb-3">
                {layouts.map((layout, index) => {
                  const prevStockType = index > 0 ? (layouts[index - 1].stockTypeId || null) : null;
                  const curStockType = layout.stockTypeId || null;
                  const showDivider = index > 0 && curStockType !== prevStockType;
                  return (
                    <React.Fragment key={index}>
                      {showDivider && <div className="w-px h-3 bg-gray-300 mx-0.5" />}
                      <button
                        onClick={() => setCurrentSheet(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentSheet ? 'bg-sage-500' : 'bg-gray-300'
                        }`}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {/* Swipeable Card Carousel */}
            <div
              className="flex-1 relative overflow-hidden"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="flex h-full transition-transform duration-300 ease-out"
                style={{ transform: `translateX(calc(-${currentSheet * 100}% + ${swipeOffset}px))` }}
              >
                {layouts.map((layout, index) => {
                  const stockTypeId = layout?.stockTypeId;
                  const stockType = stockTypeId
                    ? stockSheetTypes?.find(t => t.id === stockTypeId)
                    : null;
                  const w = layout?.width || sheetWidth;
                  const l = layout?.length || sheetLength;
                  const sheetsInGroup = layouts.filter(l => (l.stockTypeId || null) === (stockTypeId || null));
                  const indexInGroup = sheetsInGroup.indexOf(layout) + 1;

                  // Group pieces by name
                  const partCounts = new Map();
                  (layout?.pieces || []).forEach(piece => {
                    const name = piece.name || 'Unnamed';
                    if (!partCounts.has(name)) {
                      const color = getPartColor(piece.originalId).hex;
                      partCounts.set(name, { count: 0, color, originalId: piece.originalId });
                    }
                    partCounts.get(name).count += 1;
                  });
                  const parts = [];
                  partCounts.forEach((val, name) => parts.push({ name, ...val }));

                  return (
                    <div key={layout.id || index} className="w-full flex-shrink-0 px-1">
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
                        {/* Stock Label */}
                        <div className="text-center mb-3">
                          {stockType && (
                            <h2 className="text-lg font-bold text-gray-900">{stockType.name}</h2>
                          )}
                          <p className="text-sm text-gray-500">
                            Sheet {indexInGroup} of {sheetsInGroup.length}
                            <span className="mx-1">•</span>
                            {w.toFixed(1)} × {l.toFixed(1)} {getUnitLabel()}
                            {layout?.thicknessLabel && (
                              <span className="ml-1 text-gray-400">• {layout.thicknessLabel}</span>
                            )}
                          </p>
                        </div>

                        {/* Sheet Layout */}
                        <div className="flex items-center justify-center flex-1">
                          <SheetRenderer
                            layout={layout}
                            mode="interactive"
                            maxWidth={isLandscape ? 500 : window.innerWidth - 80}
                            showLabels={false}
                            showDimensions={false}
                            highlightedPieceId={highlightedPieceId}
                            onPieceHover={setHighlightedPieceId}
                          />
                        </div>

                        {/* Pieces on this sheet */}
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">
                            {layout?.pieces.length || 0} Pieces on this sheet
                          </h3>
                          <div className="flex flex-wrap justify-start gap-x-2 gap-y-1.5">
                            {parts.map((part, i) => {
                              const isHovered = highlightedPieceId === part.originalId;
                              return (
                                <div
                                  key={i}
                                  className={`inline-flex items-center gap-1.5 text-xs transition-all cursor-pointer rounded-full px-2.5 py-1 whitespace-nowrap border ${
                                    isHovered
                                      ? 'text-gray-900 font-semibold bg-gray-50'
                                      : 'text-gray-700'
                                  }`}
                                  style={{
                                    borderColor: part.color,
                                    backgroundColor: `${part.color}20`,
                                  }}
                                  onMouseEnter={() => setHighlightedPieceId?.(part.originalId)}
                                  onMouseLeave={() => setHighlightedPieceId?.(null)}
                                >
                                  <span>{part.name}</span>
                                  <span className="text-gray-400">×{part.count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>


          </div>
        )}
      </div>
    </div>
  );
};

export default LayoutStep;
